import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from '@mui/icons-material';

const FilePreview = ({ file, open, onClose }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (file && open) {
      setLoading(true);
      
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setLoading(false);
        
        return () => URL.revokeObjectURL(url);
      } else if (file.type === 'application/pdf') {
        // Para PDFs, mostraremos un mensaje indicativo
        setPreviewUrl(null);
        setLoading(false);
      }
    }
  }, [file, open]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!file) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Box>
          <Typography variant="h6" component="div">
            {file.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatFileSize(file.size)} • {file.type}
          </Typography>
        </Box>
        
        {file.type.startsWith('image/') && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
            >
              <ZoomOutIcon />
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center', minWidth: 50, textAlign: 'center' }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Button
              size="small"
              onClick={handleZoomIn}
              disabled={zoom >= 3}
            >
              <ZoomInIcon />
            </Button>
          </Box>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        overflow: 'auto',
        p: 2
      }}>
        {loading ? (
          <CircularProgress />
        ) : file.type.startsWith('image/') ? (
          <Box sx={{ 
            textAlign: 'center',
            maxWidth: '100%',
            maxHeight: '100%',
            overflow: 'auto'
          }}>
            <img
              src={previewUrl}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `scale(${zoom})`,
                transition: 'transform 0.2s ease',
                objectFit: 'contain'
              }}
              onLoad={() => setLoading(false)}
            />
          </Box>
        ) : file.type === 'application/pdf' ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" gutterBottom>
              📄 Documento PDF
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Vista previa de PDF no disponible en el navegador.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              El archivo se procesará correctamente cuando envíes el formulario.
            </Typography>
          </Box>
        ) : (
          <Typography variant="body1" color="textSecondary">
            Vista previa no disponible para este tipo de archivo.
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreview;